import { type FC, useCallback, useContext } from "react";
import { Navigate, generatePath } from "react-router-dom";

import { SimpleGrid, Stack } from "@mantine/core";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useIntl } from "react-intl";

import jpegBannerImageUrl from "@app-assets/jpg/banner-bg.jpg";
import webpBannerImageUrl from "@app-assets/webp/banner-bg.webp";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { AbsoluteRoute } from "../../core/routes";
import { RouteEntities } from "../../core/routes/relativeRoutes";
import { CofConfigId, MetricField } from "../../gql-generated/types";
import { PageHeader, PageSubHeader } from "../../layout/components";
import { SelectTimeRange } from "../../lens-insights/components/select-time-range";
import { LensGranularity } from "../../lens-insights/models";
import { UserInfoContext } from "../../shared/api/contexts";
import { BannerContainer } from "../../shared/components";
import { PayoutTosBanner } from "../../shared/components/tos";
import { usePageRedirect } from "../../shared/hooks";
import { useDefaultOrganizationId } from "../../shared/hooks/useDefaultOrganizationId";
import { Feature } from "../../shared/models";
import { useMergeTopLensesWithInfo, useTopLensesByPayout } from "../hooks";

import { HomeMadeWithLensesSection } from "./HomeMadeWithLensesSection";
import { headerContainer, titleContainer } from "./HomePage.module.css";
import { HomeStatsRow } from "./HomeStatsRow";
import { HomeTopLensesSection } from "./HomeTopLensesSection";
import { HomeTrendingSection } from "./HomeTrendingSection";
import {
    HomeSubtitle,
    TableHeaderPayout,
    TableHeaderReach,
    TopLensesByPayouts,
    TopLensesByPayoutsSubtitle,
    TopLensesByReach,
    TopLensesByReachSubtitle,
    WelcomeTitle,
    messages,
} from "./messages.generated";

// Persist the homepage banner dismissal per user so one account dismissing it on a
// shared browser does not hide it for another. `getOnInit` reads storage synchronously
// at atom creation so a previously dismissed banner does not flash on first render.
const bannerDismissedByUserAtom = atomWithStorage<Record<string, boolean>>(
    "my-lenses-home-banner-dismissed-by-user",
    {},
    undefined,
    { getOnInit: true },
);

export const HomePage: FC = () => {
    const redirect = usePageRedirect(Feature.home);
    const intl = useIntl();
    const [bannerDismissedByUser, setBannerDismissedByUser] = useAtom(bannerDismissedByUserAtom);
    const { configs } = useFeatureGates();
    const isMadeWithLensesEnabled = configs[CofConfigId.MyLensesHomeTab].enabled;
    const userInfo = useContext(UserInfoContext);
    const name = userInfo?.data?.getUserProfile.userDisplayName ?? "";
    const userId = userInfo?.data?.getUserProfile.userId;

    const isBannerDismissed = userId ? bannerDismissedByUser[userId] === true : false;

    const handleBannerDismiss = useCallback(() => {
        if (!userId) {
            return;
        }
        setBannerDismissedByUser((previous) => ({ ...previous, [userId]: true }));
    }, [userId, setBannerDismissedByUser]);
    const { data: topLensesByReach, loading: topLensesByReachLoading } = useMergeTopLensesWithInfo(
        MetricField.LensViewers,
    );
    const { data: topLensesByPayout, loading: topLensesByPayoutLoading } = useTopLensesByPayout();

    const orgId = useDefaultOrganizationId();

    const engagementHref = generatePath(AbsoluteRoute.ORG_INSIGHTS_TAB, {
        orgId,
        insightsTab: RouteEntities.Engagement,
    });
    const monetizationHref = generatePath(AbsoluteRoute.ORG_INSIGHTS_TAB, {
        orgId,
        insightsTab: RouteEntities.Monetization,
    });

    if (redirect) {
        return <Navigate to={redirect} />;
    }

    return (
        <Stack gap="xl" pb="lg">
            {!isBannerDismissed && (
                <BannerContainer
                    configId={CofConfigId.MyLensesBanner}
                    dataTestId="home-banner"
                    webpImageUrl={webpBannerImageUrl}
                    jpegImageUrl={jpegBannerImageUrl}
                    dismissLabel={intl.formatMessage(messages.leaderboardBannerDismiss)}
                    onDismiss={handleBannerDismiss}
                />
            )}
            <div className={headerContainer}>
                <div className={titleContainer}>
                    <PageHeader>
                        <WelcomeTitle name={name} />
                    </PageHeader>
                    <PageSubHeader>
                        <HomeSubtitle />
                    </PageSubHeader>
                </div>
                <SelectTimeRange defaultRange={LensGranularity.Lifetime} />
            </div>
            <PayoutTosBanner />
            <HomeStatsRow />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                <HomeTopLensesSection
                    title={<TopLensesByReach />}
                    subtitle={<TopLensesByReachSubtitle />}
                    valueColumnLabel={<TableHeaderReach />}
                    data={topLensesByReach}
                    loading={topLensesByReachLoading}
                    seeAllHref={engagementHref}
                    insightsTab={RouteEntities.Engagement}
                />
                <HomeTopLensesSection
                    title={<TopLensesByPayouts />}
                    subtitle={<TopLensesByPayoutsSubtitle />}
                    valueColumnLabel={<TableHeaderPayout />}
                    data={topLensesByPayout}
                    loading={topLensesByPayoutLoading}
                    seeAllHref={monetizationHref}
                    insightsTab={RouteEntities.Monetization}
                />
            </SimpleGrid>
            <HomeTrendingSection />
            {isMadeWithLensesEnabled && <HomeMadeWithLensesSection />}
        </Stack>
    );
};
